import axios, { type AxiosResponse } from 'axios'
import type { CurrencyCode } from '@/constants/currencies'
import type { SupportedCurrencyCode } from '@/constants/supportedCurrencies'
import BaseService from '../BaseService'

// The Zuora Payment Page config (pageId / tenant / Hosted Domain) is environment-specific.
// The backend selects the page based on the `env` query param:
//   (none)      → production page (Hosted Domain: studio.trivago.com)
//   env=dev     → dev page (Hosted Domain: studio.dev.trv.cloud)
//   env=preview → preview page (*.stage.kube.trv.cloud, separate page from dev)
// Per-PR preview deployments must use the dedicated preview page whose Hosted Domain,
// with "Allow Subdomain Callback", covers those hosts.
const DEV_ZUORA_HOSTNAME = 'studio.dev.trv.cloud'
const PREVIEW_ZUORA_DOMAIN_SUFFIX = '.stage.kube.trv.cloud'

export function getZuoraEnv(hostname = window.location.hostname): 'dev' | 'preview' | null {
  if (hostname === DEV_ZUORA_HOSTNAME) return 'dev'
  if (hostname.endsWith(PREVIEW_ZUORA_DOMAIN_SUFFIX)) return 'preview'
  return null
}

import { require } from '@/utils/guards'

// ==================== Type Definitions ====================

/** Bill to contact information */
export interface BillToContact {
  address1: string
  city: string
  country: string
  firstName: string
  lastName: string
  state: string
  workEmail: string
  zipCode: string
  taxRegion?: string | null
  VATId?: string | null
}

/** Tax information */
export interface TaxInfo {
  exemptCertificateId: string
  vatId?: string | null
  exemptStatus: 'Yes' | 'No'
}

/** Create Zuora account body */
export interface CreateZuoraAccountBody {
  name: string
  currency: CurrencyCode
  thm_item_id__c: string
  hpmCreditCardPaymentMethodId: string
  billToContact: BillToContact
  taxInfo: TaxInfo
  paymentMethodType?: PaymentMethodType
}

/** Update Zuora account body */
export interface UpdateZuoraAccountBody {
  name?: string
  currency?: CurrencyCode
  defaultPaymentMethodId?: string
  billToContact: BillToContact
  taxInfo: TaxInfo
  thm_item_id__c?: string
  paymentMethodType?: PaymentMethodType
}

/** Zuora account response */
export interface ZuoraAccountResponse {
  basicInfo: ZuoraBasicInfo | null
  billingAndPayment: ZuoraBillingAndPayment | null
  metrics: ZuoraMetrics | null
  billToContact: ZuoraContact | null
  soldToContact: ZuoraContact | null
  taxInfo: ZuoraTaxInfo | null
  success: boolean | null
}

export interface ZuoraBasicInfo {
  id: string | null
  name: string | null
  accountNumber: string | null
  notes: string | null
  status: string | null
  crmId: string | null
  batch: string | null
  invoiceTemplateId: string | null
  communicationProfileId: string | null
  profileNumber: string | null
  purchaseOrderNumber: string | null
  customerServiceRepName: string | null
  thm_taxcountry__c: string | null
  thm_taxkey__c: string | null
  transaction_ip__c: string | null
  organization_id__c: string | null
  thm_account_id__c: string | null
  thm_account_type__c: string | null
  thm_product_name__c: string | null
  thm_customer_id__c: string | null
  cnpj_cpf_id_number__c: string | null
  thm_billing_cycle__c: string | null
  thm_item_id__c: string | null
  salesRep: string | null
  parentId: string | null
  creditMemoTemplateId: string | null
  debitMemoTemplateId: string | null
  summaryStatementTemplateId: string | null
  sequenceSetId: string | null
}

export interface ZuoraBillingAndPayment {
  billCycleDay: number | null
  currency: string | null
  paymentTerm: string | null
  paymentGateway: string | null
  paymentGatewayNumber: string | null
  defaultPaymentMethodId: string | null
  invoiceDeliveryPrefsPrint: boolean | null
  invoiceDeliveryPrefsEmail: boolean | null
  autoPay: boolean | null
  additionalEmailAddresses: string[] | null
  paymentMethodCascadingConsent: boolean | null
}

export interface ZuoraMetrics {
  balance: number | null
  totalInvoiceBalance: number | null
  creditBalance: number | null
  totalDebitMemoBalance: number | null
  unappliedPaymentAmount: number | null
  unappliedCreditMemoAmount: number | null
  contractedMrr: number | null
}

export interface ZuoraContact {
  accountId: string | null
  accountNumber: string | null
  address1: string | null
  address2: string | null
  city: string | null
  country: string | null
  county: string | null
  fax: string | null
  firstName: string | null
  homePhone: string | null
  id: string | null
  lastName: string | null
  mobilePhone: string | null
  nickname: string | null
  otherPhone: string | null
  otherPhoneType: string | null
  personalEmail: string | null
  state: string | null
  taxRegion: string | null
  workEmail: string | null
  workPhone: string | null
  zipCode: string | null
  contactDescription: string | null
}

export interface ZuoraTaxInfo {
  exemptStatus: string | null
  exemptCertificateId: string | null
  exemptCertificateType: string | null
  exemptIssuingJurisdiction: string | null
  exemptEffectiveDate: string | null
  exemptExpirationDate: string | null
  exemptDescription: string | null
  exemptEntityUseCode: string | null
  companyCode: string | null
  VATId: string | null
}

/** Payment method types */
export type PaymentMethodType = 'CREDIT_CARD' | 'SEPA' | 'WIRE_TRANSFER' | 'PAYPAL'

/** Payment method base */
export interface PaymentMethod {
  id: string
  type: PaymentMethodType
  isDefault: boolean
  status: string
}

/** Credit card payment method */
export interface CreditCardPaymentMethod extends PaymentMethod {
  type: 'CREDIT_CARD'
  accountHolderInfo: AccountHolderInfo | null
  cardNumber: string
  creditCardType: string
  expirationMonth: number
  expirationYear: number
  expired: boolean
}

/** SEPA payment method */
export interface SEPAPaymentMethod extends PaymentMethod {
  type: 'SEPA'
  accountHolderInfo: AccountHolderInfo | null
  accountNumber: string
}

/** Wire transfer payment method */
export interface WireTransferPaymentMethod extends PaymentMethod {
  type: 'WIRE_TRANSFER'
}

export interface AccountHolderInfo {
  accountHolderName: string | null
  country: string | null
}

/** List of payment methods response */
export interface ListPaymentMethodsResponse {
  paymentMethods: PaymentMethod[]
}

/** RSA signature for a Zuora hosted payment page */
export interface ZuoraHostedPageRsaSignature {
  signature: string
  token: string
  tenantId: string
  key: string
  zuoraHppAppUrl: string | null
}

/** A Zuora hosted payment page (used to render the payment form) — NOT an actual saved payment method */
export interface ZuoraHostedPaymentPage {
  pageId: string
  pageType: string
  rsaSignature: ZuoraHostedPageRsaSignature | null
}

/** PayPal configuration, returned by GET /zuora/payment-methods when PayPal is available */
export interface PayPalConfig {
  zuoraPaymentGatewayId: string
  zuoraPublishableKey: string
  paypalClientId: string
}

/** Response from GET /zuora/payment-methods */
export interface AvailablePaymentMethodsResponse {
  paymentMethods: ZuoraHostedPaymentPage[]
  /** Present only when PayPal is enabled for the given currency/env */
  paypal?: PayPalConfig
}

/** Payload for PUT /zuora/account/paypal/payment-session */
export interface PayPalPaymentSessionPayload {
  amount: number
  currency: string
}

/** Response from PUT /zuora/account/paypal/payment-session */
export interface PayPalPaymentSessionResponse {
  token?: string
}

/** Tax ID validation payload */
export interface TaxIdValidationPayload {
  taxId: string
  country: string
  itemId?: string | null
}

/** Tax ID validation error type */
export type TaxValidationErrorType =
  | 'TAX_ID_REGEX_VALIDATION_FAILURE'
  | 'TAX_ID_REMOTE_VALIDATION_FAILURE'
  | 'TAX_ID_CPF_CNPJ_VALIDATION_FAILURE'

/** Tax ID validation response */
export interface TaxValidationResponse {
  valid: boolean
  errorType: TaxValidationErrorType | null
  taxIdExample: string | null
}

/** Plus insights response */
export interface PlusInsightsResponse {
  badgeViews: number | null
  badgeClicks: number | null
  websiteClicks: number | null
}

/** Invoice payment status */
export type InvoicePaymentStatus = 'PAID' | 'PAYMENT_DUE' | 'OVERDUE'

/** Invoice response */
export interface Invoice {
  invoiceId: string
  invoiceNumber: string
  invoiceDate: string
  amount: number
  currency: CurrencyCode
  accommodationId: number | null
  productType: string | null
  status: InvoicePaymentStatus
  dueDate: string
}

/** Currency response */
export interface CurrencyResponse {
  currency: CurrencyCode
}

/** Locale info response, shared by getGuestCurrency and getUserCurrency */
export interface LocaleResponse {
  country: string | null
  currency: CurrencyCode | null
  currencySource: 'DEFAULT' | 'GEOLOCATION' | 'ZUORA' | null
  language: {
    languageCode: string | null
    languageDisplayName: string | null
  } | null
}

/** Preview price response (per-billing-cycle amount for an existing subscription) */
export interface PreviewItemPriceResponse {
  amount: number | null
  amountWithoutTax: number | null
  nextRenewalDate: string | null
  isActive: boolean
  isCancelled: boolean
}

/** Error metrics body, logged to Zuora's error log for SOX control */
export interface ErrorMetricsBody {
  itemId: number
  productName: string
  code: string
  message: string
  requestSource?: string
}

/** Subscription billing term */
export type SubscriptionTerm = 'MONTHLY' | 'YEARLY' | 'USAGE'

/** Product type */
export type ProductType = 'PLUS' | 'PRO' | 'RATE_CONNECT' | 'UNKNOWN'

/** Premium campaign, returned by the campaign create/get/cancel endpoints */
export interface PremiumCampaign {
  itemId: number
  partnerId: number
  zuoraSubscriptionName: string
  createdAt: string
  updatedAt: string
  endDate: string | null
  cancellationReason: string | null
  deactivatedAt: string | null
  term: SubscriptionTerm
  type: ProductType
  discount: number | null
  subscriptionStartDate?: string
  nextPaymentDate?: string
}

/** Voucher applied to a premium price */
export interface ItemPriceVoucher {
  voucherCode: string
  discountPercentage: number | null
  ratePlanId: string | null
  isValid: boolean
  invalidReason: 'VOUCHER_NOT_FOUND' | 'VOUCHER_NOT_ACTIVE' | null
}

/** Premium campaign price response */
export interface PriceResponse {
  chargeFrequency: SubscriptionTerm
  currency: CurrencyCode
  totalPrice: number | null
  totalPriceBeforeTaxes: number | null
  totalTaxAmount: number | null
  baseSubscriptionMonthlyPrice: number | null
  baseSubscriptionFullTermPrice: number | null
  discountAmount: number | null
  itemPriceVoucher: ItemPriceVoucher | null
}

/** Studio Plus price response */
export interface StudioPlusPriceResponse {
  chargeFrequency: SubscriptionTerm
  currency: SupportedCurrencyCode
  totalPrice: number | null
  totalPriceBeforeTaxes: number | null
  totalTaxAmount: number | null
  baseSubscriptionMonthlyPrice: number | null
  baseSubscriptionFullTermPrice: number | null
  discountAmount: number | null
  itemPriceVoucher: ItemPriceVoucher | null
}

/** A campaign entry in the admin campaigns list */
export interface CampaignResponse {
  itemId: number
  productName: string
  type: string
  endDate: string | null
  zuoraSubscriptionName: string | null
  campaignType: string | null
}

export interface CampaignsListResponse {
  campaigns: CampaignResponse[]
}

/** Product ownership status */
export type ProductOwnershipStatus =
  | 'ACTIVE'
  | 'CANCELLED'
  | 'EXPIRED'
  | 'PAUSED_FOR_BUDGET'
  | 'PAUSED_UNTIL'
  | 'PAUSED'
  | 'OBSOLETE'
  | 'UNKNOWN'

/** A single owned product entry */
export interface ProductOwnershipDTO {
  itemId: number
  partnerId: number
  startDate: string
  endDate: string | null
  term: SubscriptionTerm | null
  nextPaymentDate: string | null
  nextPaymentAmount: number | null
  currency: string | null
  subscriptionNumber: string | null
  productType: ProductType
  productName: string
  status: ProductOwnershipStatus
}

/** Paginated map of accommodationId (as string) to its owned products */
export interface PremiumPriceDetailsResponse {
  items: Record<string, ProductOwnershipDTO[]>
  totalCount: number
  pageSize: number
  pageNumber: number
  totalPages: number
}

// Request interfaces
export interface AccommodationRequest {
  accommodationId: string
}

export interface ZuoraAccountCreateRequest {
  body: CreateZuoraAccountBody
  language?: string
}

export interface ZuoraAccountUpdateRequest {
  body: UpdateZuoraAccountBody
  language?: string
}

export interface StudioPlusPriceParams {
  accommodationId: number
  countryCode?: string | null
  postalCode?: string | null
  state?: string | null
  vatId?: string | null
  currency?: SupportedCurrencyCode | null
  voucherCode?: string | null
}

export interface CreateCampaignBody {
  itemName: string
  partnerId: number
  email: string
  language: string
  studioUserId: string
  firstName: string
  lastName: string
  voucherCode?: string | null
  subscriptionSource?: string | null
}

export interface CancelCampaignBody {
  itemName?: string | null
  email?: string | null
  reason?: string | null
  partnerId: number
  language: string
  studioUserId: string
  firstName: string
  lastName: string
  additionalInformation?: string | null
}

export interface AccommodationBodyRequest<T = CreateCampaignBody | CancelCampaignBody> {
  accommodationId: string
  body: T
}

export interface UploadFutureUsagesRequest {
  subscriptionName: string
}

export interface PremiumPriceDetailsParams {
  rateConnectStatuses?: ProductOwnershipStatus[]
  plusStatuses?: ProductOwnershipStatus[]
  proStatuses?: ProductOwnershipStatus[]
  accommodationIds?: string[]
  pageNumber?: number
  pageSize?: number
}

export interface InvoiceQueryParams {
  accommodationIds?: string[]
  productTypes?: ProductType[]
  paymentStatuses?: InvoicePaymentStatus[]
  startDate?: string
  endDate?: string
}

export interface PlusPerformanceRequest {
  accommodationId: string
  startDate?: string
  endDate?: string
}

// ==================== Service Class ====================

class PremiumService extends BaseService {
  constructor() {
    super({
      baseUrl: import.meta.env.VITE_PREMIUM_API_URL,
    })
  }

  fetchZuoraAccount(): Promise<AxiosResponse<ZuoraAccountResponse>> {
    return axios({
      url: `${this.baseUrl}/zuora/account`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  sendZuoraAccountCreation({ body, language }: ZuoraAccountCreateRequest): Promise<AxiosResponse<ZuoraAccountResponse>> {
    return axios({
      url: `${this.baseUrl}/zuora/account`,
      method: 'POST',
      data: body,
      headers: {
        ...this.studioHeadersWithAttribution(),
        'X-Trv-Language': language ?? 'en-US',
      },
    })
  }

  sendZuoraAccountUpdate({ body, language }: ZuoraAccountUpdateRequest): Promise<AxiosResponse<ZuoraAccountResponse>> {
    return axios({
      url: `${this.baseUrl}/zuora/account`,
      method: 'PUT',
      data: body,
      headers: {
        ...this.studioHeadersWithAttribution(),
        'X-Trv-Language': language ?? 'en-US',
      },
    })
  }

  fetchStudioPlusCampaign({ accommodationId }: AccommodationRequest): Promise<AxiosResponse<PremiumCampaign>> {
    require(accommodationId)

    return axios({
      url: `${this.baseUrl}/plus/${accommodationId}/campaign`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  fetchAvailablePaymentMethods(currencyCode: string): Promise<AxiosResponse<AvailablePaymentMethodsResponse>> {
    const params: Record<string, string> = {
      currencyCode,
    }
    const zuoraEnv = getZuoraEnv()
    if (zuoraEnv) params['env'] = zuoraEnv

    return axios({
      url: `${this.baseUrl}/zuora/payment-methods`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
      params,
    })
  }

  createPayPalPaymentSession({
    amount,
    currency,
  }: PayPalPaymentSessionPayload): Promise<AxiosResponse<PayPalPaymentSessionResponse>> {
    return axios({
      url: `${this.baseUrl}/zuora/account/paypal/payment-session`,
      method: 'PUT',
      data: { amount, currency },
      headers: this.studioHeadersWithAttribution(),
    })
  }

  fetchExistingPaymentMethods(): Promise<AxiosResponse<ListPaymentMethodsResponse>> {
    return axios({
      url: `${this.baseUrl}/zuora/account/payment-methods`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  validateTaxId(payload: TaxIdValidationPayload): Promise<AxiosResponse<TaxValidationResponse>> {
    return axios({
      url: `${this.baseUrl}/validation/tax`,
      method: 'POST',
      data: payload,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  fetchStudioPlusPrice(params: StudioPlusPriceParams): Promise<AxiosResponse<PriceResponse>> | undefined {
    if (!params.accommodationId) return undefined

    const searchParams: Record<string, any> = {}

    for (const key in params) {
      const value = params[key as keyof StudioPlusPriceParams]
      if (key !== 'accommodationId' && value !== undefined && value !== null && value !== '') {
        searchParams[key] = value
      }
    }

    return axios({
      url: `${this.baseUrl}/plus/${params.accommodationId}/price`,
      method: 'GET',
      params: searchParams,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  fetchPreviewPrice({ accommodationId, productType }: { accommodationId: string; productType: string }): Promise<AxiosResponse<PreviewItemPriceResponse>> | undefined {
    if (!accommodationId) return undefined

    return axios({
      url: `${this.baseUrl}/plus/${accommodationId}/price/preview`,
      method: 'GET',
      params: { productType },
      headers: this.studioHeadersWithAttribution(),
    })
  }

  sendStudioPlusSubscription({ accommodationId, body }: AccommodationBodyRequest<CreateCampaignBody>): Promise<AxiosResponse<PremiumCampaign>> {
    require(accommodationId)

    return axios({
      url: `${this.baseUrl}/plus/${accommodationId}/campaign`,
      method: 'POST',
      data: body,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  cancelSubscription({ accommodationId, body }: AccommodationBodyRequest<CancelCampaignBody>): Promise<AxiosResponse<PremiumCampaign>> {
    require(accommodationId)

    return axios({
      url: `${this.baseUrl}/plus/${accommodationId}/deactivate`,
      method: 'PUT',
      data: body,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  getPaidSubscriptions(accommodationId: string): Promise<AxiosResponse<CampaignsListResponse>> {
    require(accommodationId)

    return axios({
      url: `${this.baseUrl}/admin/${accommodationId}/campaigns`,
      method: 'GET',
      headers: this.studioHeaders(),
    })
  }

  uploadFutureUsages({ subscriptionName }: UploadFutureUsagesRequest): Promise<AxiosResponse<void>> {
    return axios({
      url: `${this.baseUrl}/admin/finance_tool/upload_usages/${subscriptionName}`,
      method: 'POST',
      headers: this.studioHeaders(),
    })
  }

  sendErrorMetrics(body: ErrorMetricsBody): Promise<AxiosResponse> {
    return axios({
      url: `${this.baseUrl}/zuora/error/log`,
      method: 'POST',
      data: body,
      headers: this.studioHeaders(),
    })
  }

  /** No authentication required */
  getGuestCurrency(): Promise<AxiosResponse<LocaleResponse>> {
    return axios({
      url: `${this.baseUrl}/locale/guest`,
      method: 'GET',
    })
  }

  getUserCurrency(): Promise<AxiosResponse<LocaleResponse>> {
    return axios({
      url: `${this.baseUrl}/locale/user`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  getPremiumPriceDetails(params: PremiumPriceDetailsParams): Promise<AxiosResponse<PremiumPriceDetailsResponse>> {
    const filteringParams: Record<string, any> = {
      pageNumber: 0,
      pageSize: 10,
    }

    for (const key in params) {
      const value = params[key as keyof PremiumPriceDetailsParams]
      if (value !== undefined) {
        filteringParams[key] = value
      }
    }

    return axios({
      url: `${this.baseUrl}/ownership`,
      method: 'GET',
      params: filteringParams,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  getInvoices(params: InvoiceQueryParams): Promise<AxiosResponse<Invoice[]>> {
    const filteringParams: Record<string, any> = {}

    for (const key in params) {
      const value = params[key as keyof InvoiceQueryParams]
      if (value !== undefined) {
        filteringParams[key] = value
      }
    }

    return axios({
      url: `${this.baseUrl}/zuora/account/invoices`,
      method: 'GET',
      params: filteringParams,
      headers: this.studioHeadersWithAttribution(),
    })
  }

  getInvoice(invoiceId: string): Promise<AxiosResponse<Blob>> {
    return axios({
      url: `${this.baseUrl}/zuora/account/invoices/${invoiceId}`,
      method: 'GET',
      responseType: 'blob',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  getPlusPerformance({ accommodationId, startDate, endDate }: PlusPerformanceRequest): Promise<AxiosResponse<PlusInsightsResponse>> {
    require(accommodationId)

    return axios({
      url: `${this.baseUrl}/plus/${accommodationId}/insights`,
      method: 'GET',
      params: { startDate, endDate },
      headers: this.studioHeadersWithAttribution(),
    })
  }
}

export default new PremiumService()
